import { h, Component } from 'preact';

import { joinClasses } from 'utils/utils';

import HTML from 'components/core/html/html';
import Image from 'components/image/image';
import { getDefaultLandingCtaText, getReleaseMessaging } from 'components/editor/pages/focus/landing/utils';

import s from 'components/editor/pages/focus/landing/heroCarousel.sss';

export default class HeroCarousel extends Component {
	constructor(props) {
		super(props);
		
		this.scrollMomentum = 0;

		this.scrollerRef = this.scrollerRef.bind(this);
		this.onMouseEnter = this.onMouseEnter.bind(this);
		this.onMouseLeave = this.onMouseLeave.bind(this);
		this.animFrame = this.animFrame.bind(this);
		this.onScroll = this.onScroll.bind(this);
		this.scrollToItem = this.scrollToItem.bind(this);

		this.setState({
			hovered: false,
			itemSelected: false,
		});
	}

	get autoScrollPaused() {
		return this.state.hovered || this.state.itemSelected;
	}

	componentDidMount() {
		this.preciseScroll = this.$scroller.scrollWidth / 3;
		this.$scroller.scrollLeft = this.preciseScroll;
		this.lastScrollWidth = this.$scroller.scrollWidth;
		this.animFrame();
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		if (prevProps.activeElement !== this.props.activeElement) {
			const hasItemSelected = !!this.props.activeElement?.startsWith('/hero/list/');
			this.setState({ itemSelected: hasItemSelected });
			if (hasItemSelected) this.scrollToItem(this.props.activeElement.split('/').pop());
		}
	}

	componentWillUnmount() {
		cancelAnimationFrame(this.timer);
	}

	scrollerRef(ref) {
		if (ref) {
			this.$scroller = ref;
		}
	}

	onMouseEnter() {
		this.setState({ hovered: true });
	}
	
	onMouseLeave() {
		this.setState({ hovered: false });
	}

	animFrame(t) {
		cancelAnimationFrame(this.timer);
		this.timer = requestAnimationFrame(this.animFrame);

		// Adjust scroll position if content width changed (e.g. window resize)
		if (this.$scroller && this.$scroller.scrollWidth !== this.lastScrollWidth) {
			const ratio = this.$scroller.scrollWidth / this.lastScrollWidth;
			this.preciseScroll *= ratio;
			this.$scroller.scrollLeft = this.preciseScroll;
			this.lastScrollWidth = this.$scroller.scrollWidth;
		}

		if (!t) return;
		if (!this.lastFrame) {
			this.lastFrame = t;
			return;
		}

		const dt = Math.min(t - this.lastFrame, 100);
		this.lastFrame = t;
		if (Math.abs(this.scrollMomentum) > 0.001) {
			this.preciseScroll -= this.scrollMomentum * dt;
			if (this.$scroller) {
				this.$scroller.scrollLeft = this.preciseScroll;
			}
			this.scrollMomentum *= Math.pow(0.93, (dt * 60) / 1000);
		} else if (!this.autoScrollPaused) {
			this.preciseScroll += 0.05 * dt;
			if (this.$scroller) {
				this.$scroller.scrollLeft = this.preciseScroll;
			}
		}
	}

	fixInfiniteScroll() {
		const el = this.$scroller;
		const maxScroll = el.scrollWidth;
		const repeatingSize = maxScroll / 3;
		const minScroll = repeatingSize - el.clientWidth / 2;

		if (el.scrollLeft < minScroll) {
			el.style.scrollSnapType = 'none';
			el.scrollLeft += repeatingSize;
			this.preciseScroll += repeatingSize;
			el.style.scrollSnapType = '';
		} else if (el.scrollLeft > maxScroll - repeatingSize) {
			el.style.scrollSnapType = 'none';
			el.scrollLeft -= repeatingSize;
			this.preciseScroll -= repeatingSize;
			el.style.scrollSnapType = '';
		}
	}

	onScroll() {
		this.fixInfiniteScroll();
		if (this.$scroller.scrollLeft !== Math.round(this.preciseScroll)) {
			this.preciseScroll = this.$scroller.scrollLeft;
			this.snapTimer = 0;
		}
	}

	scrollToItem(index) {
		if (!this.$scroller) return;

		const targetItem = this.$scroller.querySelector(`[data-carousel-item="actual-${index}"]`);
		if (!targetItem) return;

		const scrollerRect = this.$scroller.getBoundingClientRect();
		const itemRect = targetItem.getBoundingClientRect();
		const scrollOffset = itemRect.left - scrollerRect.left - 24;
		
		this.preciseScroll = this.$scroller.scrollLeft + scrollOffset;
		this.$scroller.scrollLeft = this.preciseScroll;
	}

	renderItem(movie, index, type) {
		const releaseYear = movie?.dates?.release && (new Date(movie.dates.release).getFullYear() + ' ');
		// getReleaseMessaging now checks for global override, then auto-generates
		const releaseMessaging = getReleaseMessaging(movie);
		const ctaText = movie.ctaText || getDefaultLandingCtaText(movie);

		return (
			<div
				class={joinClasses(s.item, index % 2 && s.odd)}
				data-carousel-item={`${type}-${movie.index}`}
				key={`${type}-${index}-${movie.slug}`}
				tabIndex={type === 'actual' ? '0' : '-1'}
				{...this.props}
			>
				<div class={s.visual}>
					{!!movie.hero?.still && (
						<Image
							alt={movie.hero.still.alt || `Poster for ${movie.title} (${releaseYear || ''}Movie)`}
							focalPoint={movie.hero.still.focalPoint}
							src={movie.hero.still.url}
						/>
					)}
				</div>
				<div class={s.infoWrapper}>
					<div class={s.info}>
						<HTML class={s.title} markdown inline>
							{movie.displayTitle || movie.title}
						</HTML>
						<div class={s.details}>
							{releaseMessaging && <span>{releaseMessaging}</span>}
							{ctaText && <span>{ctaText}</span>}
						</div>
					</div>
				</div>
			</div>
		);
	}

	render(props) {
		let movies = props.list?.map((m, i) => {
			const movieData = props.movies?.[m.movie];
			if (!movieData) return null;
			return { ...m, ...movieData, index: i };
		}).filter(Boolean);

		// Ensure there's an even number of movies for better visual balance
		if (movies?.length % 2 === 1) {
			movies = movies.concat(movies);
		}

		return (
			<div class={s.wrapper}>
				<div
					class={s.scroller}
					ref={this.scrollerRef}
					onMouseEnter={this.onMouseEnter}
					onMouseLeave={this.onMouseLeave}
					onScroll={this.onScroll}
				>
					{movies?.map((m, i) => this.renderItem(m, i, 'prev'))}
					{movies?.map((m, i) => this.renderItem(m, i, 'actual'))}
					{movies?.map((m, i) => this.renderItem(m, i, 'next'))}
					{!movies?.length && (
						<div class={joinClasses(s.item, s.empty)}>
							<span>No movies added yet</span>
						</div>
					)}
				</div>
			</div>
		);
	}
}
